import { Button, Card, Image as AntImage, Image, Skeleton } from 'antd';
import Meta from 'antd/lib/card/Meta';
import Text from 'antd/lib/typography/Text';
import React, { useCallback, useMemo } from 'react';
import SpaceChar from '../Helpers/SpaceChar';
import { StoreItem } from '../../services/api/GraphqlTypes';
import { useAddToCart } from '../../services/hooks/useShoppingCart';
import resolvePublicUrl from '../../services/helpers/resolvePublicUrl';
import GoldIcon from 'components/Common/GoldIcon';
import useStoreItemModal from './useStoreItemModal';
import useUser from 'services/hooks/useUser';
import useCountdown from 'services/hooks/useCountdown';
import BluredOverlay from 'components/Helpers/BlurOverlay';

interface Props {
    key?: string;
    storeItem: StoreItem;
    selected?: boolean;
    disabled?: boolean;
    skeleton?: boolean;
}

const StoreItemCard: React.FC<Props> = (props) => {
    const { storeItem: item, selected, disabled, skeleton, key } = props;
    const [addToCart, { loading }] = useAddToCart(item as StoreItem);
    const [user] = useUser();

    const { modal, open } = useStoreItemModal({
        storeItem: item,
        onAddToCart: useCallback((close) => {
            addToCart();
            close();
        }, [addToCart]),
        loading,
        disabled
    });

    const openModal = () => {
        !skeleton && open();
    };

    const { countdown, countdownEnded } = useCountdown(item.expiresAt);

    const actions = useMemo(() => {
        return [
            skeleton ? <SpaceChar /> : (
                <GoldIcon
                    suffix={(
                        <div style={{ position: "relative" }}>
                            <Text type={item.compareAtPrice ? "success" : undefined} strong>{item.price}</Text>
                            {(item.compareAtPrice || 0) > 0 && <>
                                <SpaceChar /><SpaceChar />
                                <Text type="secondary" delete>{item.compareAtPrice}</Text>
                            </>}

                            {item.compareAtPrice ? (
                                <div
                                    style={{
                                        position: "absolute",
                                        borderRadius: "50%",
                                        backgroundColor: "red",
                                        top: -23,
                                        right: -36,
                                        padding: "4px 8px",
                                        color: "#fff",
                                        fontSize: 12
                                    }}
                                >SALE</div>
                            ) : null}
                        </div>
                    )}
                />
            ),
            skeleton ? <Skeleton.Button size="small" style={{ width: 100 }} active /> : (
                <Button
                    type="link"
                    loading={loading}
                    disabled={!user || loading || disabled || (item.useStock && !item.stock) || countdownEnded}
                    onClick={(e) => {
                        e.stopPropagation();
                        addToCart();
                    }}
                    size="small"
                // icon={<ShoppingCartOutlined />}
                >
                    {countdownEnded ? "EXPIRED" : "ADD TO CART"}
                </Button>
            )
        ]
    }, [skeleton, item, loading, disabled, addToCart, countdownEnded]);

    const avatar = useMemo(() => {
        const img = (item.image || "").startsWith("http") ? item.image : resolvePublicUrl(item.image || "");
        const paths = item.image!.split("/");
        paths.splice(paths.length - 1, 0, "thumbs");
        const thumb = paths.join("/");

        return skeleton ? <Skeleton.Image /> : (
            <div
                style={{ height: 100 }}
            >
                <AntImage
                    src={img}
                    placeholder={(
                        <Image
                            src={thumb}
                            width={100}
                        />
                    )}
                    width={100}
                    preview={false}
                    fallback="data:image/png;base64,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"
                />
            </div>
        );
    }, [skeleton, item]);

    return (
        <>
            <Card
                key={key}
                style={selected ? {
                    boxShadow: "0 1px 2px -2px rgb(110 0 130 / 64%), 0 3px 6px 0 rgb(110 0 130 / 48%), 0 5px 12px 4px rgb(110 0 130 / 36%)"
                } : undefined}
                actions={actions}
                className="store-item-card"
                onClick={openModal}
                hoverable
            >
                <BluredOverlay
                    text="EXPIRED"
                    enabled={!!item.expiresAt && countdownEnded}
                >
                    <Meta
                        avatar={avatar}
                        title={skeleton
                            ? <Skeleton active title={false} paragraph={{ rows: 1 }} />
                            : <span>{item.name}</span>}
                        description={skeleton
                            ? <Skeleton active title={false} paragraph={{ rows: 3 }} />
                            : <span className="ellipse">{item.description}</span>
                        }
                    />

                    {item.useStock && (
                        <span className="remaining slow-zoom-anim">
                            {(item.stock || 0) > 0
                                ? <>Only <div className="amount zoom-anim">{item.stock}</div> left!</>
                                : "Sold out!"}
                        </span>
                    )}

                    {item.expiresAt && !countdownEnded && (
                        <span className="expires-at slow-zoom-anim">
                            {countdownEnded ? "Expired" : `Expires in ${countdown}`}
                        </span>
                    )}
                </BluredOverlay>

            </Card>
            {modal}
        </>
    );
}

export default StoreItemCard;